import React, { useState, useMemo } from "react";

// MUI per-component imports
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Collapse from "@mui/material/Collapse";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Skeleton from "@mui/material/Skeleton";
import Slider from "@mui/material/Slider";
import Typography from "@mui/material/Typography";

// MUI icons
import AssignmentTurnedInRoundedIcon from "@mui/icons-material/AssignmentTurnedInRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import QuizRoundedIcon from "@mui/icons-material/QuizRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

// App utilities
import Langs, { Localize } from "../../../app/lang/langs";

// ─── Constants ────────────────────────────────────────────────────────────────

const BRAND_ORANGE = "#FF9015";
const ALERT_RED = "#EF4444";
const EASE = "cubic-bezier(0.4, 0, 0.2, 1)";

// ─── Utility helpers ──────────────────────────────────────────────────────────

function toNum(val) {
    const n = Number(val);
    return isNaN(n) ? 0 : n;
}

function getRateColor(rate) {
    if (rate >= 80) return "#22C55E";
    if (rate >= 50) return BRAND_ORANGE;
    return ALERT_RED;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function KpiCard({ icon, label, value, borderColor, isAlert, loading }) {
    if (loading) {
        return (
            <Card
                elevation={0}
                sx={{
                    borderRadius: "14px",
                    border: "1px solid rgba(0,40,128,0.07)",
                    borderLeft: "4px solid rgba(0,0,0,0.08)",
                    bgcolor: "#fff",
                    height: "100%",
                }}
            >
                <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Skeleton variant="rounded" width={32} height={32} sx={{ mb: 1 }} />
                    <Skeleton variant="text" width="40%" height={32} />
                    <Skeleton variant="text" width="70%" height={14} />
                </CardContent>
            </Card>
        );
    }

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: "14px",
                border: "1px solid rgba(0,40,128,0.07)",
                borderLeft: `4px solid ${borderColor}`,
                bgcolor: "#fff",
                height: "100%",
                transition: `all 200ms ${EASE}`,
                "&:active": { transform: "scale(0.97)" },
            }}
        >
            <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 32,
                        height: 32,
                        borderRadius: "9px",
                        bgcolor: `${borderColor}14`,
                        mb: 0.75,
                    }}
                >
                    {React.cloneElement(icon, {
                        sx: { fontSize: 18, color: borderColor },
                    })}
                </Box>
                <Typography
                    sx={{
                        fontWeight: 800,
                        fontSize: "1.5rem",
                        lineHeight: 1.1,
                        color: isAlert ? ALERT_RED : "rgba(15,15,15,0.88)",
                        letterSpacing: "-0.03em",
                    }}
                >
                    {value}
                </Typography>
                <Typography
                    sx={{
                        fontSize: "0.72rem",
                        color: "#64748b",
                        lineHeight: 1.3,
                        mt: 0.25,
                    }}
                >
                    {label}
                </Typography>
            </CardContent>
        </Card>
    );
}

function TeamCard({ row, totalField, completedField, rateField, totalLabel, completedLabel, rateLabel }) {
    const rate = toNum(row[rateField]);
    const color = getRateColor(rate);

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: "14px",
                border: "1px solid rgba(0,40,128,0.07)",
                bgcolor: "#fff",
                mb: 1.5,
                transition: `all 200ms ${EASE}`,
                "&:active": { transform: "scale(0.99)" },
            }}
        >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                {/* Team name */}
                <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#1a1a2e", mb: 1.5 }}>
                    {row.team_name}
                </Typography>

                {/* Stats row */}
                <Box sx={{ display: "flex", gap: 2, mb: 1.5 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography
                            variant="caption"
                            sx={{ color: "rgba(100,116,139,0.7)", fontSize: "0.72rem", display: "block", mb: 0.25 }}
                        >
                            {totalLabel}
                        </Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: "rgba(51,65,85,0.85)" }}>
                            {toNum(row[totalField])}
                        </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography
                            variant="caption"
                            sx={{ color: "rgba(100,116,139,0.7)", fontSize: "0.72rem", display: "block", mb: 0.25 }}
                        >
                            {completedLabel}
                        </Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: "rgb(22,163,74)" }}>
                            {toNum(row[completedField])}
                        </Typography>
                    </Box>
                </Box>

                {/* Rate bar */}
                <Box>
                    <Typography
                        variant="caption"
                        sx={{ color: "rgba(100,116,139,0.7)", fontSize: "0.72rem", display: "block", mb: 0.5 }}
                    >
                        {rateLabel}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
                        <Box sx={{ flex: 1 }}>
                            <LinearProgress
                                variant="determinate"
                                value={rate}
                                sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    bgcolor: "rgba(0,40,128,0.06)",
                                    "& .MuiLinearProgress-bar": {
                                        borderRadius: 4,
                                        bgcolor: color,
                                        transition: `transform 600ms ${EASE}`,
                                    },
                                }}
                            />
                        </Box>
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 600,
                                fontSize: "0.78rem",
                                color,
                                minWidth: 36,
                                textAlign: "right",
                            }}
                        >
                            {rate}%
                        </Typography>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}

function TeamSkeletonCards() {
    return (
        <Box>
            {Array.from({ length: 3 }).map((_, i) => (
                <Card
                    key={i}
                    elevation={0}
                    sx={{
                        borderRadius: "14px",
                        border: "1px solid rgba(0,40,128,0.07)",
                        bgcolor: "#fff",
                        mb: 1.5,
                    }}
                >
                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                        <Skeleton variant="text" width="50%" height={20} sx={{ mb: 1 }} />
                        <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
                            <Skeleton variant="text" width="30%" height={28} />
                            <Skeleton variant="text" width="30%" height={28} />
                        </Box>
                        <Skeleton variant="rounded" width="100%" height={8} />
                    </CardContent>
                </Card>
            ))}
        </Box>
    );
}

function EmptyState({ message }) {
    return (
        <Box
            sx={{
                py: 6,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
            }}
        >
            <SecurityRoundedIcon sx={{ fontSize: 48, color: "rgba(0,0,0,0.12)" }} />
            <Typography
                variant="body2"
                sx={{
                    color: "rgba(0,0,0,0.35)",
                    fontStyle: "italic",
                    fontSize: "0.9rem",
                    textAlign: "center",
                    px: 2,
                }}
            >
                {message}
            </Typography>
        </Box>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function VSecuriteDashboardMobile({ summary, byTeam, loading, lang, exporting, onExport }) {
    const [activeTab, setActiveTab] = useState(0);
    const [filterExpanded, setFilterExpanded] = useState(false);
    const [awarenessRateRange, setAwarenessRateRange] = useState([0, 100]);
    const [quizRateRange, setQuizRateRange] = useState([0, 100]);

    // ── Derived values ────────────────────────────────────────────────────────

    const s = {
        campaigns: toNum(summary?.total_campaigns_published),
        campaignsRate: toNum(summary?.total_campaigns_completed_rate),
        quizzes: toNum(summary?.total_quizzes_published),
        quizzesRate: toNum(summary?.total_quizzes_passed_rate),
        pendingAwareness: toNum(summary?.employees_with_pending_awareness),
        pendingQuizzes: toNum(summary?.employees_with_pending_quizzes),
    };

    const hasTeamData = Array.isArray(byTeam) && byTeam.length > 0;

    const filteredAwarenessRows = useMemo(
        () =>
            Array.isArray(byTeam)
                ? byTeam.filter((r) => {
                      const rate = toNum(r.awareness_rate);
                      return rate >= awarenessRateRange[0] && rate <= awarenessRateRange[1];
                  })
                : [],
        [byTeam, awarenessRateRange]
    );

    const filteredQuizRows = useMemo(
        () =>
            Array.isArray(byTeam)
                ? byTeam.filter((r) => {
                      const rate = toNum(r.quizzes_rate);
                      return rate >= quizRateRange[0] && rate <= quizRateRange[1];
                  })
                : [],
        [byTeam, quizRateRange]
    );

    // ── Slider styles ─────────────────────────────────────────────────────────

    const sliderSx = {
        color: BRAND_ORANGE,
        "& .MuiSlider-thumb": {
            width: 22,
            height: 22,
            transition: `box-shadow 200ms ${EASE}`,
            "&:hover, &.Mui-focusVisible": {
                boxShadow: "0 0 0 6px rgba(255,144,21,0.16)",
            },
        },
        "& .MuiSlider-valueLabel": {
            bgcolor: BRAND_ORANGE,
            borderRadius: "8px",
            fontSize: "16px",
        },
        "& .MuiSlider-rail": {
            bgcolor: "rgba(0,40,128,0.08)",
        },
    };

    // ── Helpers ───────────────────────────────────────────────────────────────

    const currentRateRange = activeTab === 0 ? awarenessRateRange : quizRateRange;
    const setCurrentRateRange = activeTab === 0 ? setAwarenessRateRange : setQuizRateRange;
    const currentRows = activeTab === 0 ? filteredAwarenessRows : filteredQuizRows;

    const awarenessFields = {
        totalField: "awareness_total",
        completedField: "awareness_completed",
        rateField: "awareness_rate",
    };
    const quizFields = {
        totalField: "quizzes_total",
        completedField: "quizzes_passed",
        rateField: "quizzes_rate",
    };
    const currentFields = activeTab === 0 ? awarenessFields : quizFields;

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <Box
            sx={{
                mt: 0,
                pb: 3,
                px: 1.5,
                "@keyframes mDashFadeIn": {
                    from: { opacity: 0, transform: "translateY(12px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                },
                animation: `mDashFadeIn 400ms ${EASE} both`,
            }}
        >
            {/* ── Header row ──────────────────────────────────────────────────── */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: "1.1rem",
                            color: "rgba(15,15,15,0.88)",
                            letterSpacing: "-0.02em",
                            lineHeight: 1.2,
                        }}
                    >
                        <Langs str="VSEC_DASHBOARD_TITLE" />
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "0.78rem",
                            color: "#64748b",
                            mt: 0.25,
                            lineHeight: 1.3,
                        }}
                    >
                        <Langs str="VSEC_DASHBOARD_SUBTITLE" />
                    </Typography>
                </Box>
                <IconButton
                    onClick={onExport}
                    disabled={exporting || loading}
                    sx={{
                        color: BRAND_ORANGE,
                        width: 44,
                        height: 44,
                        transition: `all 200ms ${EASE}`,
                        "&:active": { transform: "scale(0.92)" },
                        "&.Mui-disabled": { color: "rgba(255,144,21,0.35)" },
                    }}
                >
                    <DownloadRoundedIcon />
                </IconButton>
            </Box>

            {/* ── KPI Cards (2x2 grid) ────────────────────────────────────────── */}
            <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                <Grid item xs={6}>
                    <KpiCard
                        icon={<AssignmentTurnedInRoundedIcon />}
                        label={Localize("VSEC_CARD_CAMPAIGNS_PUBLISHED", lang)}
                        value={s.campaigns}
                        borderColor={BRAND_ORANGE}
                        loading={loading && !summary}
                    />
                </Grid>
                <Grid item xs={6}>
                    <KpiCard
                        icon={<QuizRoundedIcon />}
                        label={Localize("VSEC_CARD_QUIZZES_PUBLISHED", lang)}
                        value={s.quizzes}
                        borderColor={BRAND_ORANGE}
                        loading={loading && !summary}
                    />
                </Grid>
                <Grid item xs={6}>
                    <KpiCard
                        icon={<HourglassEmptyRoundedIcon />}
                        label={Localize("VSEC_CARD_PENDING_AWARENESS", lang)}
                        value={s.pendingAwareness}
                        borderColor={s.pendingAwareness > 0 ? ALERT_RED : BRAND_ORANGE}
                        isAlert={s.pendingAwareness > 0}
                        loading={loading && !summary}
                    />
                </Grid>
                <Grid item xs={6}>
                    <KpiCard
                        icon={<WarningAmberRoundedIcon />}
                        label={Localize("VSEC_CARD_PENDING_QUIZZES", lang)}
                        value={s.pendingQuizzes}
                        borderColor={s.pendingQuizzes > 0 ? ALERT_RED : BRAND_ORANGE}
                        isAlert={s.pendingQuizzes > 0}
                        loading={loading && !summary}
                    />
                </Grid>
            </Grid>

            {/* ── Team breakdown section ───────────────────────────────────────── */}
            <Box sx={{ mt: 2.5 }}>
                {/* Pill toggle */}
                <Box
                    sx={{
                        display: "flex",
                        bgcolor: "rgba(0,40,128,0.04)",
                        borderRadius: "12px",
                        p: "3px",
                    }}
                >
                    <Button
                        fullWidth
                        disableElevation
                        onClick={() => {
                            setActiveTab(0);
                            setFilterExpanded(false);
                        }}
                        sx={{
                            borderRadius: "10px",
                            textTransform: "none",
                            fontWeight: activeTab === 0 ? 700 : 500,
                            fontSize: "0.84rem",
                            minHeight: 44,
                            color: activeTab === 0 ? BRAND_ORANGE : "#64748b",
                            bgcolor: activeTab === 0 ? "#fff" : "transparent",
                            boxShadow: activeTab === 0 ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                            transition: `all 250ms ${EASE}`,
                            "&:hover": {
                                bgcolor: activeTab === 0 ? "#fff" : "rgba(0,40,128,0.06)",
                            },
                            "&:active": { transform: "scale(0.97)" },
                        }}
                    >
                        {Localize("VSEC_TAB_AWARENESS", lang)}
                    </Button>
                    <Button
                        fullWidth
                        disableElevation
                        onClick={() => {
                            setActiveTab(1);
                            setFilterExpanded(false);
                        }}
                        sx={{
                            borderRadius: "10px",
                            textTransform: "none",
                            fontWeight: activeTab === 1 ? 700 : 500,
                            fontSize: "0.84rem",
                            minHeight: 44,
                            color: activeTab === 1 ? BRAND_ORANGE : "#64748b",
                            bgcolor: activeTab === 1 ? "#fff" : "transparent",
                            boxShadow: activeTab === 1 ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                            transition: `all 250ms ${EASE}`,
                            "&:hover": {
                                bgcolor: activeTab === 1 ? "#fff" : "rgba(0,40,128,0.06)",
                            },
                            "&:active": { transform: "scale(0.97)" },
                        }}
                    >
                        {Localize("VSEC_TAB_QUIZ", lang)}
                    </Button>
                </Box>

                {/* Filter row */}
                <Box sx={{ mt: 1.5 }}>
                    <Button
                        size="small"
                        startIcon={<FilterAltRoundedIcon />}
                        endIcon={
                            <ExpandMoreRoundedIcon
                                sx={{
                                    transform: filterExpanded ? "rotate(180deg)" : "rotate(0deg)",
                                    transition: `transform 200ms ${EASE}`,
                                }}
                            />
                        }
                        onClick={() => setFilterExpanded((prev) => !prev)}
                        sx={{
                            color: BRAND_ORANGE,
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.82rem",
                            minHeight: 44,
                        }}
                    >
                        {Localize("VSEC_BTN_FILTER", lang)}
                    </Button>

                    <Collapse in={filterExpanded}>
                        <Box sx={{ px: 1, pb: 2, pt: 0.5 }}>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 600,
                                    color: "rgba(100,116,139,0.9)",
                                    mb: 0.5,
                                    display: "block",
                                    fontSize: "16px",
                                }}
                            >
                                {activeTab === 0
                                    ? Localize("VSEC_COL_AWARENESS_RATE", lang)
                                    : Localize("VSEC_COL_QUIZ_RATE", lang)}
                                : {currentRateRange[0]}% — {currentRateRange[1]}%
                            </Typography>
                            <Slider
                                value={currentRateRange}
                                onChange={(_, val) => setCurrentRateRange(val)}
                                min={0}
                                max={100}
                                step={5}
                                valueLabelDisplay="auto"
                                valueLabelFormat={(v) => `${v}%`}
                                sx={sliderSx}
                            />
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                <Typography variant="caption" sx={{ color: "rgba(100,116,139,0.7)" }}>
                                    ≥ {currentRateRange[0]}%
                                </Typography>
                                <Typography variant="caption" sx={{ color: "rgba(100,116,139,0.7)" }}>
                                    ≤ {currentRateRange[1]}%
                                </Typography>
                            </Box>
                        </Box>
                    </Collapse>
                </Box>

                {/* Team cards list */}
                <Box
                    sx={{
                        overflowY: "auto",
                        WebkitOverflowScrolling: "touch",
                        maxHeight: "calc(100vh - 420px)",
                        pb: 1,
                        mt: 1,
                    }}
                >
                    {loading && !hasTeamData ? (
                        <TeamSkeletonCards />
                    ) : !hasTeamData ? (
                        <EmptyState message={Localize("VSEC_EMPTY_TEAM_TABLE", lang)} />
                    ) : currentRows.length === 0 ? (
                        <EmptyState message={Localize("VSEC_EMPTY_TEAM_TABLE", lang)} />
                    ) : (
                        currentRows.map((row) => (
                            <TeamCard
                                key={row.team_name}
                                row={row}
                                totalField={currentFields.totalField}
                                completedField={currentFields.completedField}
                                rateField={currentFields.rateField}
                                totalLabel={
                                    activeTab === 0
                                        ? Localize("VSEC_COL_ASSIGNED_AWARENESS", lang)
                                        : Localize("VSEC_COL_ASSIGNED_QUIZZES", lang)
                                }
                                completedLabel={
                                    activeTab === 0
                                        ? Localize("VSEC_COL_COMPLETED", lang)
                                        : Localize("VSEC_COL_PASSED", lang)
                                }
                                rateLabel={
                                    activeTab === 0
                                        ? Localize("VSEC_COL_AWARENESS_RATE", lang)
                                        : Localize("VSEC_COL_QUIZ_RATE", lang)
                                }
                            />
                        ))
                    )}
                </Box>
            </Box>
        </Box>
    );
}
